import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { AlertTriangle, Archive, ArchiveRestore, Loader2 } from 'lucide-react';
import { CHEMINS, ficheDuCentre } from '../../../lib/navigation/chemins';
import {
  archiverCentre,
  colonnesJpaPresentes,
  creerCentre,
  lireCentre,
  lireCentres,
  lireEcoles,
  modifierCentre,
  supprimerCentre,
  usageDuCentre,
  type Ecole,
  type UsageDuCentre,
} from '../../../lib/centres/gateway.supabase';
import {
  DESCRIPTION_DU_STATUT,
  SAISIE_VIDE,
  codeSaisi,
  erreursDeSaisie,
  messageDeLaBase,
  saisieDuCentre,
  type Centre,
  type ChampDuCentre,
  type SaisieDeCentre,
  type StatutDeCentre,
} from '../../../lib/centres/regles';
import { jourABruxelles } from '../../../lib/jpa/regles';
import ClassesDuCentre from './ClassesDuCentre';

/*
  Un centre : sa création, puis sa fiche.

  Ce qu'on y programme se choisit : centre de vacances (stages et journées
  pédagogiques) ou journées pédagogiques seulement — un stage ne se programme
  que dans un centre de vacances, la base le refuse ailleurs (VALIDÉ
  ÉCLOSION, 20/10/2026). Un centre où un stage reste à venir le reste.

  Le code entre dans les numéros de facture (`CDV-<code>-…`, `JPA-<code>-…`) :
  2 à 10 majuscules ou chiffres, unique. Le changer ne renumérote pas les
  factures déjà émises.

  Un centre qui n'a jamais eu de session se supprime ; les autres s'archivent.
*/

const etiquette = 'block text-sm font-semibold text-gray-700 mb-1';

export default function AdminCentreFichePage() {
  const { centreId } = useParams<{ centreId: string }>();
  const creation = !centreId;
  const navigate = useNavigate();
  const location = useLocation();

  const [centre, setCentre] = useState<Centre | null>(null);
  const [saisie, setSaisie] = useState<SaisieDeCentre>(SAISIE_VIDE);
  const [codesDesAutres, setCodesDesAutres] = useState<string[]>([]);
  const [ecoles, setEcoles] = useState<Ecole[]>([]);
  const [avecJpa, setAvecJpa] = useState(true);
  const [usage, setUsage] = useState<UsageDuCentre | null>(null);
  const [erreurs, setErreurs] = useState<Partial<Record<ChampDuCentre, string>>>({});
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmerSuppression, setConfirmerSuppression] = useState(false);

  /* Le message laissé par la création, une fois sur la fiche. */
  useEffect(() => {
    const m = (location.state as { message?: string } | null)?.message;
    if (m) setMessage(m);
  }, [location.state]);

  const charger = useCallback(async () => {
    setErreur(null);
    const [tous, ecolesLues, jpa] = await Promise.all([lireCentres(true), lireEcoles(), colonnesJpaPresentes()]);
    setEcoles(ecolesLues);
    setAvecJpa(jpa);
    setCodesDesAutres(tous.filter((c) => c.id !== centreId).map((c) => c.code));
    if (!centreId) {
      setCentre(null);
      setSaisie(SAISIE_VIDE);
      setUsage(null);
      return;
    }
    const lu = await lireCentre(centreId);
    if (!lu) throw new Error('Ce centre est introuvable.');
    setCentre(lu);
    setSaisie(saisieDuCentre(lu));
    setUsage(await usageDuCentre(centreId, jourABruxelles(new Date())));
  }, [centreId]);

  useEffect(() => {
    setChargement(true);
    charger()
      .catch((e) => setErreur(messageDeLaBase(e)))
      .finally(() => setChargement(false));
  }, [charger]);

  /* Une ancre (`#classes`, `#ecole`) ouvre la fiche sur sa section. */
  useEffect(() => {
    if (chargement || !location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [chargement, location.hash]);

  const champ = <K extends keyof SaisieDeCentre>(k: K, v: SaisieDeCentre[K]) => {
    setSaisie((avant) => ({ ...avant, [k]: v }));
    setErreurs((avant) => ({ ...avant, [k]: undefined }));
  };

  const enregistrer = async (ev: FormEvent) => {
    ev.preventDefault();
    const e = erreursDeSaisie(saisie, codesDesAutres);
    setErreurs(e);
    setMessage(null);
    if (Object.keys(e).length > 0) {
      setErreur('Certains champs sont à corriger.');
      return;
    }
    setErreur(null);
    setEnCours(true);
    try {
      if (creation) {
        const id = await creerCentre(saisie, avecJpa);
        navigate(ficheDuCentre(id, avecJpa ? 'classes' : undefined), {
          replace: true,
          state: { message: avecJpa
            ? 'Centre créé. Si on y organise des journées pédagogiques, ajoutez ses classes ci-dessous.'
            : 'Centre créé.' },
        });
      } else if (centreId) {
        await modifierCentre(centreId, saisie, avecJpa);
        await charger();
        setMessage('Centre enregistré.');
      }
    } catch (err) {
      setErreur(messageDeLaBase(err));
    } finally {
      setEnCours(false);
    }
  };

  const archiver = async (archive: boolean) => {
    if (!centreId) return;
    setEnCours(true);
    setErreur(null);
    setMessage(null);
    try {
      await archiverCentre(centreId, archive);
      await charger();
      setMessage(archive ? 'Centre archivé.' : 'Centre désarchivé.');
    } catch (err) {
      setErreur(messageDeLaBase(err));
    } finally {
      setEnCours(false);
    }
  };

  const supprimer = async () => {
    if (!centreId) return;
    setEnCours(true);
    setErreur(null);
    try {
      await supprimerCentre(centreId);
      navigate(CHEMINS.planification.centres, { replace: true });
    } catch (err) {
      setErreur(messageDeLaBase(err));
      setConfirmerSuppression(false);
    } finally {
      setEnCours(false);
    }
  };

  if (chargement) {
    return <div className="flex justify-center items-center min-h-[40vh]"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>;
  }
  if (!creation && !centre) {
    return (
      <div className="max-w-xl space-y-4">
        <p className="text-red-700">{erreur ?? 'Ce centre est introuvable.'}</p>
        <Link to={CHEMINS.planification.centres} className="btn-outline">Retour aux centres</Link>
      </div>
    );
  }

  /* Un centre de vacances où un stage reste à venir le reste. */
  const verrouCentreDeVacances = !creation && centre?.centreDeVacances === true && (usage?.stagesAVenir ?? 0) > 0;
  const code = codeSaisi(saisie.code) || 'CODE';

  return (
    <div className="max-w-4xl space-y-5">
      <div>
        <Link to={CHEMINS.planification.centres} className="text-sm font-semibold text-primary-700 hover:underline">← Centres</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">{creation ? 'Nouveau centre' : centre?.nom}</h1>
        {!creation && centre && (
          <p className="text-gray-600 mt-1">
            Code {centre.code}
            {usage ? ` · ${usage.sessions} session${usage.sessions > 1 ? 's' : ''}, dont ${usage.journeesPedagogiques} journée${usage.journeesPedagogiques > 1 ? 's' : ''} pédagogique${usage.journeesPedagogiques > 1 ? 's' : ''}` : ''}
          </p>
        )}
      </div>

      {centre?.archive && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex flex-wrap items-center justify-between gap-3">
          <p className="text-amber-900">Ce centre est archivé : il n’apparaît plus dans les listes. Ses sessions et ses factures restent.</p>
          <button type="button" disabled={enCours} onClick={() => void archiver(false)} className="btn-outline flex items-center disabled:opacity-50">
            <ArchiveRestore className="h-4 w-4 mr-2" aria-hidden="true" />
            Désarchiver
          </button>
        </div>
      )}
      {erreur && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-2" role="alert">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p>{erreur}</p>
        </div>
      )}
      {message && <p className="p-3 bg-green-50 text-green-800 rounded-lg" role="status">{message}</p>}

      <form onSubmit={(e) => void enregistrer(e)} className="space-y-5" noValidate>
        <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="text-lg font-bold">Le centre</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <label className="block md:col-span-2">
              <span className={etiquette}>Nom du centre</span>
              <input value={saisie.nom} onChange={(e) => champ('nom', e.target.value)} className="form-input" aria-invalid={!!erreurs.nom} />
              {erreurs.nom && <span className="block text-sm text-red-700 mt-1">{erreurs.nom}</span>}
            </label>
            <label className="block">
              <span className={etiquette}>Code</span>
              <input
                value={saisie.code}
                onChange={(e) => champ('code', e.target.value.toUpperCase())}
                maxLength={10}
                autoCapitalize="characters"
                className="form-input uppercase"
                aria-invalid={!!erreurs.code}
                aria-describedby="aide-code"
              />
              {erreurs.code
                ? <span id="aide-code" className="block text-sm text-red-700 mt-1">{erreurs.code}</span>
                : (
                  <span id="aide-code" className="block text-xs text-gray-600 mt-1">
                    Dans les numéros de facture : CDV-{code}-… et JPA-{code}-…{!creation && ' Le changer ne renumérote pas les factures déjà émises.'}
                  </span>
                )}
            </label>
          </div>
          <label className="block max-w-xs">
            <span className={etiquette}>Téléphone</span>
            <input type="tel" value={saisie.telephone} onChange={(e) => champ('telephone', e.target.value)} placeholder="+32 470 12 34 56" className="form-input" />
            <span className="block text-xs text-gray-600 mt-1">Cité dans les informations avant le stage.</span>
          </label>
        </section>

        <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="text-lg font-bold">Adresse</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block md:col-span-2">
              <span className={etiquette}>Adresse</span>
              <input value={saisie.adresse} onChange={(e) => champ('adresse', e.target.value)} className="form-input" aria-invalid={!!erreurs.adresse} />
              {erreurs.adresse && <span className="block text-sm text-red-700 mt-1">{erreurs.adresse}</span>}
            </label>
            <label className="block md:col-span-2">
              <span className={etiquette}>Adresse du second site (si le centre en a un)</span>
              <input value={saisie.adresse2} onChange={(e) => champ('adresse2', e.target.value)} className="form-input" />
            </label>
            <label className="block">
              <span className={etiquette}>Code postal</span>
              <input inputMode="numeric" value={saisie.codePostal} onChange={(e) => champ('codePostal', e.target.value)} className="form-input" aria-invalid={!!erreurs.codePostal} />
              {erreurs.codePostal && <span className="block text-sm text-red-700 mt-1">{erreurs.codePostal}</span>}
            </label>
            <label className="block">
              <span className={etiquette}>Localité</span>
              <input value={saisie.localite} onChange={(e) => champ('localite', e.target.value)} className="form-input" aria-invalid={!!erreurs.localite} />
              {erreurs.localite && <span className="block text-sm text-red-700 mt-1">{erreurs.localite}</span>}
            </label>
          </div>
        </section>

        <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="text-lg font-bold">Ce qu’on y programme</h2>
          {avecJpa ? (
            <fieldset className="space-y-2">
              <legend className="sr-only">Ce qu’on programme dans ce centre</legend>
              <label className={`flex items-start gap-3 p-3 rounded-lg border ${saisie.centreDeVacances ? 'border-primary-300 bg-primary-50' : 'border-gray-200'}`}>
                <input type="radio" name="usage" checked={saisie.centreDeVacances} onChange={() => champ('centreDeVacances', true)} className="mt-1 h-4 w-4" />
                <span>
                  <strong>Centre de vacances</strong> — des stages et des journées pédagogiques.
                </span>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border ${!saisie.centreDeVacances ? 'border-primary-300 bg-primary-50' : 'border-gray-200'} ${verrouCentreDeVacances ? 'opacity-60' : ''}`}>
                <input
                  type="radio"
                  name="usage"
                  checked={!saisie.centreDeVacances}
                  disabled={verrouCentreDeVacances}
                  onChange={() => champ('centreDeVacances', false)}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  <strong>Journées pédagogiques seulement</strong> — un lieu sans stages, comme l’École de la Sainte-Famille.
                </span>
              </label>
              {verrouCentreDeVacances && (
                <p className="text-sm text-amber-900">
                  {usage?.stagesAVenir} stage{(usage?.stagesAVenir ?? 0) > 1 ? 's' : ''} à venir : ce centre reste un centre de vacances tant qu’ils ont lieu.
                </p>
              )}
            </fieldset>
          ) : (
            <p className="text-sm text-amber-900">
              Les journées pédagogiques ne sont pas encore installées sur cette base : le choix de l’usage, l’école et les classes
              apparaîtront avec elles. En attendant, tout centre est un centre de vacances.
            </p>
          )}
          <label className="block max-w-md">
            <span className={etiquette}>Statut sur le site</span>
            <select value={saisie.statut} onChange={(e) => champ('statut', e.target.value as StatutDeCentre)} className="form-input">
              <option value="actif">Actif — ses stages sont publiés</option>
              <option value="pause">En pause — rien n’est publié</option>
              <option value="test">Test — centre d’essai, jamais publié</option>
            </select>
            <span className="block text-xs text-gray-600 mt-1">{DESCRIPTION_DU_STATUT[saisie.statut]}</span>
          </label>
        </section>

        {avecJpa && (
          <section id="ecole" className="bg-white rounded-xl border border-gray-200 p-5 space-y-3 scroll-mt-4">
            <h2 className="text-lg font-bold">École des élèves</h2>
            <label className="block max-w-md">
              <span className={etiquette}>École</span>
              <select value={saisie.ecoleId} onChange={(e) => champ('ecoleId', e.target.value)} className="form-input">
                <option value="">Aucune</option>
                {ecoles.map((e) => <option key={e.id} value={e.id}>{e.nom}</option>)}
              </select>
              <span className="block text-xs text-gray-600 mt-1">
                Les journées pédagogiques de ce centre sont réservées à ses élèves. Une école manque ?{' '}
                <Link to={CHEMINS.planification.ecoles} className="font-semibold text-primary-700 underline">Les écoles</Link>
              </span>
            </label>
          </section>
        )}

        <div className="flex flex-wrap gap-3">
          <button type="submit" disabled={enCours} className="btn-primary disabled:opacity-50">
            {enCours ? 'Enregistrement…' : creation ? 'Créer le centre' : 'Enregistrer'}
          </button>
          <Link to={CHEMINS.planification.centres} className="btn-outline">{creation ? 'Annuler' : 'Retour aux centres'}</Link>
        </div>
      </form>

      {avecJpa && (
        <section id="classes" className="bg-white rounded-xl border border-gray-200 p-5 space-y-4 scroll-mt-4">
          <div>
            <h2 className="text-lg font-bold">Classes</h2>
            <p className="text-sm text-gray-600">
              Au nom de l’instituteur : c’est ce que les parents reconnaissent en inscrivant leur enfant à une journée pédagogique.
              À revoir à chaque rentrée.
            </p>
          </div>
          {creation || !centreId
            ? <p className="text-sm text-gray-600">Créez d’abord le centre : ses classes s’ajoutent ensuite, ici.</p>
            : <ClassesDuCentre centreId={centreId} />}
        </section>
      )}

      {!creation && centre && (
        <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
          <h2 className="text-lg font-bold">Archiver ou supprimer</h2>
          {!centre.archive && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-700">Archivé, le centre disparaît des listes ; ses sessions et ses factures restent.</p>
              <button type="button" disabled={enCours} onClick={() => void archiver(true)} className="btn-outline flex items-center disabled:opacity-50">
                <Archive className="h-4 w-4 mr-2" aria-hidden="true" />
                Archiver
              </button>
            </div>
          )}
          {usage && usage.sessions > 0 ? (
            <p className="text-sm text-gray-700">
              Ce centre a {usage.sessions} session{usage.sessions > 1 ? 's' : ''} : il ne se supprime pas, il s’archive.
            </p>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-700">Aucune session n’y a jamais été programmée : le centre peut être supprimé.</p>
              <button type="button" disabled={enCours} onClick={() => setConfirmerSuppression(true)} className="btn-outline text-red-700 border-red-300 hover:bg-red-50 disabled:opacity-50">
                Supprimer le centre
              </button>
            </div>
          )}
        </section>
      )}

      <Dialog open={confirmerSuppression} onClose={() => !enCours && setConfirmerSuppression(false)} className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-40" />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <Dialog.Title className="text-lg font-bold text-gray-900">Supprimer « {centre?.nom} » ?</Dialog.Title>
            <p className="text-sm text-gray-700">Le centre, ses classes et ses réglages (communautés WhatsApp, facturation aux communes) sont effacés. Cela ne se défait pas.</p>
            <div className="flex justify-end gap-3">
              <button type="button" disabled={enCours} onClick={() => setConfirmerSuppression(false)} className="btn-outline">Annuler</button>
              <button type="button" disabled={enCours} onClick={() => void supprimer()} className="btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50">
                {enCours ? 'Suppression…' : 'Supprimer le centre'}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
